/**
 * Client-side PostHog consent application.
 *
 * Consented persistence is localStorage only — never localStorage+cookie —
 * so PostHog identity is not written as a first-party cookie that can
 * trigger Azure Front Door WAF SQLI false positives.
 */

import { clearPostHogCookies } from "@/lib/utils/cookie-consent";
import posthog from "posthog-js";

/**
 * Logs and exception autocapture are optional products. They stay blocked
 * until analytics consent, including a consent cookie that was already set
 * before `posthog.init`. Surveys stay disabled in init (`disable_surveys`).
 */
let optionalPostHogScriptsAllowed = false;

export function allowOptionalPostHogScripts(allowed: boolean): void {
  optionalPostHogScriptsAllowed = allowed;
}

const DEFERRED_POSTHOG_SCRIPTS = ["/logs.js", "/exception-autocapture"];

export function preparePostHogExternalScript(
  script: HTMLScriptElement,
): HTMLScriptElement | null {
  if (optionalPostHogScriptsAllowed) return script;
  const src = script.src || "";
  if (DEFERRED_POSTHOG_SCRIPTS.some((name) => src.includes(name))) return null;
  return script;
}

function startOptionalPostHogProducts(): void {
  posthog.startExceptionAutocapture();
  posthog.logs?.loadIfEnabled();
}

export function applyAnalyticsConsent(granted: boolean): void {
  try {
    if (granted) {
      allowOptionalPostHogScripts(true);
      posthog.opt_in_capturing();
      posthog.set_config({ persistence: "localStorage" });
      // A logs.js fetch that was cancelled before consent does not retry
      // on its own. Exception autocapture stays off until this call because
      // init sets capture_exceptions: false.
      startOptionalPostHogProducts();
    } else {
      allowOptionalPostHogScripts(false);
      // Back to the init-time default before opting out, so the opt-out
      // record itself is not written to localStorage. Without this, a user
      // who grants and then withdraws consent leaves persistence on
      // localStorage and PostHog re-creates ph_* keys we just cleared.
      posthog.set_config({ persistence: "memory" });
      posthog.opt_out_capturing();
      posthog.stopExceptionAutocapture();
    }
    // Leftover ph_* cookies are never wanted with localStorage-only
    // persistence. Expire them on grant and deny so declined users do not
    // keep sending WAF-triggering parent-domain cookies.
    clearPostHogCookies();
  } catch {
    // PostHog may not be initialised (missing key, etc.)
  }
}
